import { Button, Dropdown, Flex, type MenuProps } from "antd";
import { useNavigate, useParams } from "react-router";
import {
  useDeleteQuizMutation,
  usePublicCourseDetailsQuery,
} from "../../../app/features/adminContent/adminContent.api";
import ArrowLeftIcon from "../../../assets/icons/ArrowLeftIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import ThreeDotIcon from "../../../assets/icons/ThreeDotIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/CustomTable";
import CustomTitle from "../../../components/CustomTitle";
import useError from "../../../hooks/useError";
import useFilters from "../../../hooks/useFilters";
import useModal from "../../../hooks/useModal";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { toast } from "../../../lib/toast";
import type { ColumnsType } from "../../../types/global.types";
import ConfirmModal from "../Dashboard/_components/ConfirmModal";
import ManageTopicsModal from "./_components/ManageTopicsModal";

export default function ManageQuizList() {
  const { courseId, sectionId } = useParams();
  const navigate = useNavigate();
  const { limit, page, handleLimit, handlePagination } = useFilters();
  const { open, handleOpen, handleClose } = useModal<any, undefined>();

  // Query API
  // const { data, isLoading, error } = useGetQuizQuery(Number(courseId), {
  //   skip: !courseId,
  // });

  // Mutation API
  const [deleteQuiz, { isLoading: deleteLoading, error: deleteError }] =
    useDeleteQuizMutation();

  // Error Handeling
  useError(deleteError, "Quiz Tpoics Deleted Failed");

  const {
    data: courseDetails,
    isLoading: courseLoading,
    error,
  } = usePublicCourseDetailsQuery(Number(courseId), {
    skip: !courseId,
  });
  useError(error, "Quiz get failed");

  const quizArray =
    courseDetails?.CourseSection?.find(
      (el) => String(el?.id) === String(sectionId),
    )?.Quiz || [];

  const handleDelete = async () => {
    if (open?.data?.id) {
      try {
        // Call the mutation with the quiz ID
        await deleteQuiz(open.data.id).unwrap(); // <- unwrap ensures we can catch errors
        toast("success", "Quiz Topics Deleted Successfully");
        handleClose();
      } catch (error) {
        if (error) {
          toast("error", "Failed to delete quiz");
        }
      }
    }
  };

  const actionMenu = (record: any): MenuProps => ({
    items: [
      {
        key: "edit",
        icon: <EditIcon />,
        label: "Edit",
        onClick: () => {
          handleOpen({
            type: "UPDATE",
            data: record, // ✅ this EXISTS in ModalType
          });
        },
      },
      {
        key: "delete",
        icon: <DeleteIcon />,
        label: "Delete",
        onClick: () => {
          handleOpen({ type: "CONFIRM", data: record });
        },
      },
      {
        key: "details",
        icon: <ViewIcon />,
        label: "Details",
        onClick: () => {
          navigate(
            `/admin/my-course/manage-course/manage-quiz/${courseId}/${sectionId}/details/${record.id}`,
          );
        },
      },
    ],
  });

  const columns: ColumnsType<any> = [
    {
      title: "Quiz Topics",
      dataIndex: "title",
      width: 300,
      render: (val) => <span>{val}</span>,
    },
    // {
    //   title: "Total Marks",
    //   dataIndex: "totalMarks",
    //   align: "center",
    //   width: 300,
    //   render: (val) => <span>{val}</span>,
    // },
    // {
    //   title: "Pass Mark Percent",
    //   dataIndex: "passMarkPercent",
    //   align: "center",
    //   width: 300,
    //   render: (val) => <span>{val}%</span>,
    // },
    {
      title: "Time Duration",
      dataIndex: "timeLimitSec",
      align: "center",
      width: 300,
      render: (val: number) => <span>{val / 60} min</span>,
    },
    // {
    //   title: "Sort Order",
    //   dataIndex: "sortOrder",
    //   align: "center",
    //   width: 300,
    //   render: (val) => <span>{val}</span>,
    // },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_: any, record: any) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Dropdown menu={actionMenu(record)} trigger={["click"]}>
            <span
              style={{ cursor: "pointer", lineHeight: 0 }}
              onClick={(e) => e.preventDefault()}
            >
              <ThreeDotIcon className="text-lg" />
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Quiz Topics Management">
      <Flex justify="space-between" align="center" className="mb-4">
        <Flex gap={16}>
          <Button
            type="default"
            size="middle"
            className="btn-bordered-primary border
               border-zinc-200 hover:!border-zinc-200 focus:!border-zinc-200 active:!border-zinc-200"
            onClick={() =>
              navigate(`/admin/my-course/manage-course/content/${courseId}`)
            }
          >
            <ArrowLeftIcon /> Back
          </Button>
          <CustomTitle
            title="Quiz Topics Management"
            description="Manage quizzes, track learners performance"
          />
        </Flex>

        <Button
          type="primary"
          className="shadow-sm"
          onClick={() => handleOpen({ type: "ADD" })}
        >
          Create Topics
        </Button>
      </Flex>

      <CustomTable
        tableTitle="Quiz Topics"
        columns={columns}
        dataSource={quizArray ?? []}
        limit={limit}
        page={page}
        total={quizArray?.length ?? 0}
        onLimitChange={handleLimit}
        onPaginate={handlePagination}
        loading={courseLoading}
        hideRowSelect
        filterSection={<></>}
      />

      {(open?.type === "ADD" || open?.type === "UPDATE") && (
        <ManageTopicsModal
          open={open?.open}
          onCancel={handleClose}
          initialValues={open?.data}
          type={open?.type}
        />
      )}

      {open?.type === "CONFIRM" && (
        <ConfirmModal
          loading={deleteLoading}
          open={open?.open}
          onCancel={handleClose}
          title="Are you sure you want to delete this item?"
          subTitle="This action cannot be undone. Once deleted, the item will be permanently removed."
          handleConfirm={handleDelete}
        />
      )}
    </DashboardLayout>
  );
}
